import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, ChevronDown, ChevronRight, MessageCircle, PhoneCall, Mail, FileText, ShieldCheck, Clock, Users, PlayCircle } from 'lucide-react';
import TopNavbar from '../components/TopNavbar';
import MobileNavbar from '../components/MobileNavbar';
import Footer from '../components/Landing/Footer';

const HelpCenterPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  const faqCategories = [
    { id: 'all', name: 'All Topics' },
    { id: 'account', name: 'Account & Billing' },
    { id: 'deals', name: 'Creating Deals' },
    { id: 'platform', name: 'Platform Usage' },
    { id: 'technical', name: 'Technical Issues' }
  ];

  const faqs = [
    {
      id: 1,
      category: 'account',
      question: 'How do I create a vendor account?',
      answer: 'Creating a vendor account is simple. Click on the "Sign Up" button at the top of the page, select "Vendor Account", and follow the registration steps. You\'ll need to provide your business details, contact information, and complete the verification process.'
    },
    {
      id: 2,
      category: 'account',
      question: 'What are the subscription options?',
      answer: 'We offer flexible subscription plans to fit businesses of all sizes. Our Basic plan is perfect for small businesses just getting started, while our Professional and Premium plans offer additional features for growing businesses. You can view detailed pricing on our Pricing page.'
    },
    {
      id: 3,
      category: 'deals',
      question: 'How do I create my first deal?',
      answer: 'After logging into your vendor dashboard, click on "Create Deal" in the navigation menu. Our intuitive deal builder will guide you through setting up your offer, including title, description, pricing, terms, and promotional images. Once submitted, your deal will be reviewed and published to our marketplace.'
    },
    {
      id: 4,
      category: 'deals',
      question: 'Can I limit the number of deal redemptions?',
      answer: 'Yes, you can set maximum redemption limits for any deal. This helps you manage inventory and avoid overselling. Simply specify the limit when creating or editing your deal in the "Redemption Settings" section.'
    },
    {
      id: 5,
      category: 'platform',
      question: 'How do I track deal performance?',
      answer: 'Our analytics dashboard provides comprehensive insights into your deal performance. You can view metrics such as views, clicks, redemptions, and revenue. Navigate to the "Analytics" section in your vendor dashboard to access these insights.'
    },
    {
      id: 6,
      category: 'technical',
      question: 'What file formats are supported for deal images?',
      answer: 'We support JPG, PNG, and WebP image formats. For optimal display, we recommend using images with a 16:9 aspect ratio and a minimum resolution of 1200 x 675 pixels. Images should be less than 5MB in size.'
    }
  ];

  const supportFeatures = [
    {
      icon: <MessageCircle className="text-[#FF6C6C]" />,
      title: 'Live Chat Support',
      description: 'Get instant answers from our support team through our live chat available during business hours.'
    },
    {
      icon: <PhoneCall className="text-[#FF6C6C]" />,
      title: 'Priority Phone Support',
      description: 'Speak directly with our dedicated vendor success team for urgent assistance.'
    },
    {
      icon: <FileText className="text-[#FF6C6C]" />,
      title: 'Comprehensive Documentation',
      description: 'Access detailed guides and tutorials on using every feature of our platform.'
    },
    {
      icon: <ShieldCheck className="text-[#FF6C6C]" />,
      title: 'Dedicated Account Manager',
      description: 'Premium vendors receive personalized support from a dedicated account manager.'
    }
  ];

  const filteredFaqs = activeTab === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <TopNavbar isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      {isMenuOpen && (
        <MobileNavbar isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      )}
      
      {/* Hero Section */}
      <section className="overflow-hidden pt-28 pb-16 bg-white">
        <div className="container px-4 mx-auto">
          <div className="mx-auto max-w-2xl text-center">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6 text-4xl font-bold md:text-5xl"
            >
              How Can We <span className="text-[#FF6C6C]">Help You?</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-8 text-lg text-gray-600"
            >
              Find answers, tutorials, and support to make the most of the Dealboxx vendor platform
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative mx-auto max-w-xl"
            >
              <div className="flex absolute inset-y-0 left-0 items-center pl-4">
                <Search className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search for help articles, tutorials, and more..."
                className="w-full py-4 pl-12 pr-4 text-gray-700 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent"
              />
              <button className="absolute inset-y-0 right-0 flex items-center px-6 text-white rounded-r-xl bg-[#FF6C6C] hover:bg-[#ff5252] transition-colors duration-300">
                Search
              </button>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Support Features */}
      <section className="py-16 bg-gray-50">
        <div className="container px-4 mx-auto">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">Dedicated Vendor Support</h2>
              <p className="text-lg text-gray-600">
                We're committed to helping your business succeed with personalized support at every step
              </p>
            </motion.div>
          </div>
          
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {supportFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md"
              >
                <div className="p-4 mb-4 bg-gray-50 rounded-lg">
                  {feature.icon}
                </div>
                <h3 className="mb-3 text-xl font-bold">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container px-4 mx-auto">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-600">
                Find quick answers to common questions about the Dealboxx vendor platform
              </p>
            </motion.div>
          </div>
          
          <div className="mx-auto max-w-4xl">
            <div className="overflow-x-auto mb-8">
              <div className="flex min-w-max">
                {faqCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveTab(category.id)}
                    className={`px-6 py-3 font-medium rounded-lg whitespace-nowrap transition-colors duration-300 ${
                      activeTab === category.id
                        ? 'bg-[#FF6C6C] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="space-y-4">
              {filteredFaqs.map((faq) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden rounded-xl border border-gray-200"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                    className="flex justify-between items-center px-6 py-4 w-full text-left"
                  >
                    <span className="font-medium">{faq.question}</span>
                    {expandedFaq === faq.id ? (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  
                  {expandedFaq === faq.id && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Video Tutorials */}
      <section className="py-16 bg-gray-50">
        <div className="container px-4 mx-auto">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">Video Tutorials</h2>
              <p className="text-lg text-gray-600">
                Learn how to make the most of our platform with step-by-step video guides
              </p>
            </motion.div>
          </div>
          
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: 'Creating Your First Deal',
                duration: '5:23',
                thumbnail: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80'
              },
              {
                title: 'Analyzing Deal Performance',
                duration: '7:45',
                thumbnail: 'https://images.unsplash.com/photo-1543286386-713bdd548da4?auto=format&fit=crop&w=800&q=80'
              },
              {
                title: 'Marketing Your Dealboxx Offers',
                duration: '6:18',
                thumbnail: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80'
              }
            ].map((video, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="overflow-hidden rounded-xl shadow-sm"
              >
                <div className="relative h-48 bg-gray-200 group">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="object-cover w-full h-full"
                  />
                  <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-40 transition-opacity duration-300 group-hover:bg-opacity-50">
                    <PlayCircle className="w-16 h-16 text-white opacity-90" />
                  </div>
                  <div className="absolute right-0 bottom-0 px-3 py-1 m-3 text-xs font-medium text-white bg-black bg-opacity-70 rounded-full">
                    {video.duration}
                  </div>
                </div>
                <div className="p-4 bg-white">
                  <h3 className="font-semibold">{video.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-10 text-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-3 font-medium text-white rounded-lg bg-[#FF6C6C] hover:bg-[#ff5252] transition-colors duration-300"
            >
              View All Tutorials
            </motion.button>
          </div>
        </div>
      </section>
      
      {/* Support Availability */}
      <section className="py-16 bg-white">
        <div className="container px-4 mx-auto">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col justify-center"
            >
              <h2 className="mb-6 text-3xl font-bold md:text-4xl">We're Here When You Need Us</h2>
              <p className="mb-6 text-lg text-gray-600">
                Our vendor support team is dedicated to helping you succeed on our platform.
                Whether you have questions about creating deals, managing your account, or
                optimizing your promotions, we're ready to assist.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="flex items-center justify-center w-12 h-12 mr-4 rounded-full bg-[#FF6C6C]/10">
                    <Clock className="text-[#FF6C6C]" />
                  </div>
                  <div>
                    <h3 className="font-medium">Support Hours</h3>
                    <p className="text-gray-600">Monday-Friday: 8am-8pm EST</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="flex items-center justify-center w-12 h-12 mr-4 rounded-full bg-[#FF6C6C]/10">
                    <Mail className="text-[#FF6C6C]" />
                  </div>
                  <div>
                    <h3 className="font-medium">Email Support</h3>
                    <p className="text-gray-600">vendors@dealboxx.com</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="flex items-center justify-center w-12 h-12 mr-4 rounded-full bg-[#FF6C6C]/10">
                    <PhoneCall className="text-[#FF6C6C]" />
                  </div>
                  <div>
                    <h3 className="font-medium">Vendor Support Line</h3>
                    <p className="text-gray-600">+1 (555) 123-4567</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="overflow-hidden relative p-8 bg-white rounded-xl border border-gray-100 shadow-lg">
                <div className="mb-6">
                  <h3 className="mb-2 text-2xl font-bold">Contact Support</h3>
                  <p className="text-gray-600">Fill out this form and we'll get back to you as soon as possible.</p>
                </div>
                
                <form>
                  <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700">Your Name</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700">Email Address</label>
                    <input
                      type="email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700">How can we help?</label>
                    <textarea
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent"
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full px-6 py-3 font-medium text-white rounded-lg bg-[#FF6C6C] hover:bg-[#ff5252] transition-colors duration-300"
                  >
                    Submit Request
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F6C] text-white">
        <div className="container px-4 mx-auto">
          <div className="mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-6 text-3xl font-bold md:text-4xl">Ready to Grow Your Business?</h2>
              <p className="mb-8 text-lg">
                Join thousands of businesses already using Dealboxx to attract new customers and increase revenue.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 font-medium text-[#FF6C6C] transition-all bg-white rounded-lg hover:shadow-lg"
                >
                  Get Started Today
                </motion.button>
                <button className="px-8 py-4 font-medium bg-white bg-opacity-20 rounded-lg transition-all hover:bg-opacity-30">
                  Take a Tour
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default HelpCenterPage; 